import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { api, ErroreApi } from '../../servizi/api.ts'
import { useSessione } from '../../servizi/sessione.tsx'
import { Avviso, Dato } from '../../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Campo, Etichetta, Modale, Riquadro, Scelta, Spunta, Tabella } from '../../componenti/base.tsx'
import { giorno, messaggioErrore } from '../../componenti/elenco.tsx'

/**
 * «Chat riservata» della scheda (decisione del committente del 29/10/2026, migrazione 480).
 *
 * Uno spazio del messenger con piu' sotto-chat cifrate, per chi puo' leggere l'area riservata della
 * scheda e, nelle sotto-chat «con la persona», per la persona assistita. Il riquadro sta nella
 * linguetta «Area riservata» (AreaRiservata.tsx) e si mostra solo a chi l'API lo concede
 * (GET /persone/:id/chat-riservata, altrimenti 404 e il riquadro non c'e').
 *
 * La Direzione (riservato.leggi con motivazione obbligatoria) vede lo stato ed entra scrivendo il
 * motivo: l'ingresso vale 30 giorni. Le estensioni della conservazione le decidono Direzione, DPO e
 * Referente privacy (482), qui e nella pagina Approvazioni (EstensioniChatRiservate, in fondo al file);
 * senza il consenso della persona solo il DPO e, dalla 499, il Referente privacy.
 */

/** Con quale ruolo si decidono le estensioni (servizi/chat-riservata.ts, ruoloDecisore). */
type Decisore = 'DIR' | 'DPO' | 'RPR'
const NOMI_DECISORI: Record<Decisore, string> = { DIR: 'Direzione', DPO: 'DPO', RPR: 'Referente privacy' }
/** Concede anche senza consenso: il DPO e il Referente privacy (499, decideSenzaConsenso dell'API). */
const senzaConsenso = (ruolo: Decisore) => ruolo === 'DPO' || ruolo === 'RPR'

type Estensione = {
  id: string; stato: 'in_attesa' | 'concessa' | 'respinta'; richiesta_il: string; richiesta_da: string
  fino_al_richiesto: string; motivazione: string | null; decisa_il: string | null; decisa_da: string | null
  ruolo_decisore: Decisore | null; fino_al: string | null; consenso: boolean | null
  consenso_modalita: string | null; nota: string | null
}

type Chat = {
  id: string; stato: 'aperta' | 'chiusa' | 'da_cancellare'; creata_il: string; creata_da: string | null
  chiusa_il: string | null; conserva_fino_al: string | null; conserva_al_massimo: string | null
  personale: boolean; ingresso_fino_al: string | null; alias: string | null; collegamento: string | null
  stanze: { id: string; nome: string; con_persona: boolean; fissa: boolean; alias: string; collegamento: string }[]
  persona: { nome_utente: string; accesso_il: string } | null
  estensioni: Estensione[]
}

type Pannello = {
  messenger: { attivo: boolean; indirizzo: string | null }
  accesso: 'incarico' | 'autorizzazione' | 'motivo'
  nel_messenger: boolean
  decide_estensioni: Decisore | null
  puo_creare: boolean
  /** La scheda ha note nell'area riservata: senza, la chat non si crea. */
  note_riservate: boolean
  conservazione: { giorni: number; anni_massimi: number }
  chat: Chat[]
}

type Credenziali = { nome_utente: string; parola: string; server: string; app: string }

type Finestra =
  | { tipo: 'crea' }
  | { tipo: 'entra'; chat: Chat }
  | { tipo: 'stanza'; chat: Chat }
  | { tipo: 'persona'; chat: Chat }
  | { tipo: 'chiudi'; chat: Chat }
  | { tipo: 'estensione'; chat: Chat }
  | { tipo: 'decidi'; estensione: Estensione; massimo: string | null }

const STATI: Record<Chat['stato'], { nome: string; tono: 'positivo' | 'neutro' | 'attenzione' }> = {
  aperta: { nome: 'Aperta', tono: 'positivo' },
  chiusa: { nome: 'Chiusa', tono: 'neutro' },
  da_cancellare: { nome: 'Da cancellare', tono: 'attenzione' },
}

const STATI_ESTENSIONE: Record<Estensione['stato'], string> = {
  in_attesa: 'In attesa', concessa: 'Concessa', respinta: 'Respinta',
}

const NON_RISPONDE = 'MESSENGER_NON_RISPONDE'

/** Esito di un comando: fatto, oppure il messenger non ha risposto (le scritture restano). */
type Esito = { testo: string; chat?: string } | { nonRisponde: string | null; testo?: string }

export function ChatRiservata({ personaId }: { personaId: string }) {
  const [dati, setDati] = useState<Pannello | 'nascosto' | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<Esito | null>(null)
  const [finestra, setFinestra] = useState<Finestra | null>(null)
  const [riprova, setRiprova] = useState(false)
  const [creando, setCreando] = useState(false)

  const carica = useCallback(async () => {
    try {
      setDati(await api.get<Pannello>(`/persone/${personaId}/chat-riservata`))
      setErrore(null)
    } catch (e) {
      // Fuori dal perimetro il riquadro non c'e': la risposta non dice nemmeno se la chat esiste.
      if (e instanceof ErroreApi && e.stato === 404) setDati('nascosto')
      else setErrore(messaggioErrore(e))
    }
  }, [personaId])
  useEffect(() => { void carica() }, [carica])

  if (dati === 'nascosto') return null
  if (!dati) return errore ? <Avviso tipo="errore">{errore}</Avviso> : null

  const fatto = (e: Esito) => { setFinestra(null); setEsito(e); void carica() }

  async function allinea(chatId: string) {
    setRiprova(true)
    try {
      await api.post(`/persone/${personaId}/chat-riservata/${chatId}/allinea`)
      setEsito({ testo: 'Il messenger ha ricevuto la chat aggiornata.' })
    } catch (e) {
      setEsito(e instanceof ErroreApi && e.codice === NON_RISPONDE ? { nonRisponde: chatId } : { nonRisponde: null, testo: messaggioErrore(e) })
    } finally { setRiprova(false) }
  }

  const base = `/persone/${personaId}/chat-riservata`
  const aperta = dati.chat.find((c) => c.stato === 'aperta')

  /** Creazione con un clic; chi entra solo con il motivo passa dalla finestra del motivo. */
  async function creaSubito() {
    setCreando(true); setErrore(null); setEsito(null)
    try { await crea(base, null, fatto, setErrore) } finally { setCreando(false) }
  }

  return (
    <>
      {esito && 'nonRisponde' in esito && (
        <Avviso tipo="attenzione">
          <div className="riga">
            <span>{esito.testo ?? 'Il messenger non risponde: riprova fra poco. Quello che hai fatto è registrato nel gestionale.'}</span>
            {esito.nonRisponde && (
              <Bottone variante="secondario" dimensione="piccolo" inCorso={riprova}
                       onClick={() => void allinea(esito.nonRisponde!)}>Riprova</Bottone>
            )}
          </div>
        </Avviso>
      )}
      {esito && !('nonRisponde' in esito) && <Avviso tipo="successo">{esito.testo}</Avviso>}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {!aperta && (
        <Riquadro titolo="Chat riservata"
                  azioni={dati.puo_creare && (
                    <Bottone variante="primario" dimensione="piccolo" inCorso={creando}
                             onClick={() => (dati.accesso === 'motivo' ? setFinestra({ tipo: 'crea' }) : void creaSubito())}>
                      Crea la chat riservata
                    </Bottone>
                  )}>
          <p className="piccolo">
            Uno spazio del messenger, cifrato, per il personale che segue questa scheda: sotto-chat dell’équipe e
            sotto-chat con la persona assistita. Nel messenger lo spazio si chiama con il solo numero della scheda.
          </p>
          {!dati.messenger.attivo && <Avviso tipo="attenzione">Il messenger non è attivo: la chat riservata non si può creare.</Avviso>}
          {dati.messenger.attivo && !dati.note_riservate && (
            <p className="piccolo tenue">
              La chat riservata si crea sulle schede che hanno note nell’area riservata: prima scrivi la nota.
            </p>
          )}
        </Riquadro>
      )}

      {dati.chat.map((c) => (
        <SchedaChat key={c.id} chat={c} dati={dati}
                    apri={setFinestra} />
      ))}

      {finestra?.tipo === 'crea' && (
        <ModaleMotivo titolo="Crea la chat riservata" comando="Crea ed entra" chiudi={() => setFinestra(null)}
                      invia={(motivazione) => crea(base, motivazione, fatto)} />
      )}
      {finestra?.tipo === 'entra' && (
        <ModaleMotivo titolo="Entra nella chat riservata" comando="Entra" chiudi={() => setFinestra(null)}
                      invia={(motivazione) => comando(`${base}/${finestra.chat.id}/ingresso`, { motivazione },
                        finestra.chat.id, 'Sei entrato nella chat per 30 giorni. Nel messenger la trovi fra le tue stanze.', fatto)} />
      )}
      {finestra?.tipo === 'stanza' && (
        <ModaleStanza chiudi={() => setFinestra(null)}
                      invia={(nome, conPersona) => comando(`${base}/${finestra.chat.id}/stanze`, { nome, con_persona: conPersona },
                        finestra.chat.id, `Sotto-chat «${nome}» creata.`, fatto)} />
      )}
      {finestra?.tipo === 'persona' && (
        <ModalePersona chat={finestra.chat} percorso={`${base}/${finestra.chat.id}/persona`}
                       chiudi={() => { setFinestra(null); void carica() }}
                       nonRisponde={() => fatto({ nonRisponde: null,
                         testo: 'Il messenger non risponde: riprova fra poco. L’accesso della persona non è pronto: rifallo quando il messenger risponde.' })} />
      )}
      {finestra?.tipo === 'chiudi' && (
        <ModaleConferma titolo="Chiudi la chat riservata" comando="Chiudi la chat" chiudi={() => setFinestra(null)}
                        invia={() => comando(`${base}/${finestra.chat.id}/chiudi`, undefined, finestra.chat.id,
                          'Chat chiusa.', fatto)}>
          <p>
            Chiusa la chat, nessuno scrive più e la persona assistita esce. Il personale la può ancora leggere per{' '}
            {dati.conservazione.giorni} giorni, poi si cancella dal server, salvo un’estensione concessa dalla
            Direzione, dal DPO o dal Referente privacy. Non si riapre: se serve, se ne crea una nuova.
          </p>
        </ModaleConferma>
      )}
      {finestra?.tipo === 'estensione' && (
        <ModaleEstensione chat={finestra.chat} decisore={dati.decide_estensioni} anniMassimi={dati.conservazione.anni_massimi}
                          percorso={`${base}/${finestra.chat.id}/estensioni`} chiudi={() => setFinestra(null)} fatto={fatto} />
      )}
      {finestra?.tipo === 'decidi' && dati.decide_estensioni && (
        <ModaleDecisione estensione={finestra.estensione} massimo={finestra.massimo} ruolo={dati.decide_estensioni}
                         chiudi={() => setFinestra(null)} fatto={(testo) => fatto({ testo })} />
      )}
    </>
  )
}

/** Creazione: un clic, oppure con il motivo per chi entra solo cosi'. */
async function crea(base: string, motivazione: string | null, fatto: (e: Esito) => void, errore?: (t: string) => void) {
  try {
    await api.post(base, motivazione ? { motivazione } : {})
    fatto({ testo: 'Chat riservata creata, con le sotto-chat «Équipe» e «Con la persona».' })
  } catch (e) {
    if (e instanceof ErroreApi && e.codice === NON_RISPONDE) {
      fatto({ nonRisponde: (e.corpo as { id?: string })?.id ?? null })
      return
    }
    if (errore) { errore(messaggioErrore(e)); return }
    throw e
  }
}

/**
 * Un comando sulla chat. Se il messenger non risponde la scrittura e' gia' fatta: la finestra si
 * chiude e l'avviso propone di riprovare l'allineamento. Gli altri errori restano nella finestra.
 */
async function comando(percorso: string, corpo: unknown, chatId: string, testo: string, fatto: (e: Esito) => void) {
  try {
    await api.post(percorso, corpo)
    fatto({ testo })
  } catch (e) {
    if (e instanceof ErroreApi && e.codice === NON_RISPONDE) { fatto({ nonRisponde: chatId }); return }
    throw e
  }
}

function SchedaChat({ chat: c, dati, apri }: { chat: Chat; dati: Pannello; apri: (f: Finestra) => void }) {
  const stato = STATI[c.stato]
  const inAttesa = c.estensioni.some((e) => e.stato === 'in_attesa')
  const comandi = (
    <>
      {c.personale && c.collegamento && (
        <Link className="bottone bottone-secondario bottone-piccolo" to={c.collegamento}>Apri nel messenger</Link>
      )}
      {!c.personale && dati.accesso === 'motivo' && c.stato !== 'da_cancellare' && (
        <Bottone variante="primario" dimensione="piccolo" onClick={() => apri({ tipo: 'entra', chat: c })}>
          Entra nella chat
        </Bottone>
      )}
    </>
  )

  return (
    <Riquadro titolo={<>Chat riservata <Etichetta tono={stato.tono}>{stato.nome}</Etichetta></>} azioni={comandi}>
      {c.personale && !dati.nel_messenger && (
        <Avviso tipo="attenzione">
          Non usi il messenger: nella chat entra solo chi ha un incarico con il messenger. Gli altri comandi restano disponibili.
        </Avviso>
      )}
      {!c.personale && dati.accesso === 'motivo' && (
        <p className="piccolo">
          Con il tuo incarico entri nella chat solo scrivendo il motivo: l’ingresso vale 30 giorni ed è registrato,
          con il motivo, nel registro degli eventi.
        </p>
      )}

      <dl className="elenco-dati">
        <Dato nome="Creata">{giorno(c.creata_il)}{c.creata_da ? ` · ${c.creata_da}` : ''}</Dato>
        {c.chiusa_il && <Dato nome="Chiusa">{giorno(c.chiusa_il)}</Dato>}
        {c.conserva_fino_al && (
          <Dato nome="Conservata">fino al {giorno(c.conserva_fino_al)}{c.conserva_al_massimo ? ` (al massimo fino al ${giorno(c.conserva_al_massimo)})` : ''}</Dato>
        )}
        {c.personale && (
          <Dato nome="Accesso della persona">
            {c.persona ? <>dal {giorno(c.persona.accesso_il)} · <span className="mono">{c.persona.nome_utente}</span></> : 'non ancora creato'}
          </Dato>
        )}
        {c.ingresso_fino_al && <Dato nome="Il tuo ingresso">fino al {giorno(c.ingresso_fino_al)}</Dato>}
      </dl>

      {c.personale && (
        <Tabella intestazioni={['Sotto-chat', 'Chi c’è', '']}>
          {c.stanze.map((s) => (
            <tr key={s.id}>
              <td data-etichetta="Sotto-chat">{s.nome}</td>
              <td data-etichetta="Chi c’è" className="piccolo">{s.con_persona ? 'Personale e persona assistita' : 'Solo personale'}</td>
              <td data-etichetta="">
                <div className="riga riga-a-destra">
                  <Link className="bottone bottone-discreto bottone-piccolo" to={s.collegamento}>Apri</Link>
                </div>
              </td>
            </tr>
          ))}
        </Tabella>
      )}

      {c.personale && c.stato === 'aperta' && (
        <div className="riga">
          <Bottone variante="secondario" dimensione="piccolo" onClick={() => apri({ tipo: 'stanza', chat: c })}>Nuova sotto-chat</Bottone>
          <Bottone variante="secondario" dimensione="piccolo" onClick={() => apri({ tipo: 'persona', chat: c })}>Accesso della persona</Bottone>
          <Bottone variante="critico" dimensione="piccolo" onClick={() => apri({ tipo: 'chiudi', chat: c })}>Chiudi la chat</Bottone>
        </div>
      )}
      {c.stato !== 'aperta' && !inAttesa && (c.personale || dati.decide_estensioni) && (
        <div className="riga">
          <Bottone variante="secondario" dimensione="piccolo" onClick={() => apri({ tipo: 'estensione', chat: c })}>
            {c.personale ? 'Chiedi un’estensione' : 'Concedi un’estensione'}
          </Bottone>
        </div>
      )}

      {c.estensioni.length > 0 && (
        <>
          <p className="persona-sottotitolo">Estensioni della conservazione</p>
          <Tabella intestazioni={['Richiesta', 'Motivo', 'Esito', '']}>
            {c.estensioni.map((e) => (
              <tr key={e.id}>
                <td data-etichetta="Richiesta" className="piccolo">
                  {giorno(e.richiesta_il)} · {e.richiesta_da}
                  <div>fino al {giorno(e.fino_al_richiesto)}</div>
                </td>
                <td data-etichetta="Motivo" className="testo-libero">{e.motivazione}</td>
                <td data-etichetta="Esito" className="piccolo">
                  <Etichetta tono={e.stato === 'concessa' ? 'positivo' : e.stato === 'respinta' ? 'critico' : 'attenzione'}>
                    {STATI_ESTENSIONE[e.stato]}
                  </Etichetta>
                  {e.stato !== 'in_attesa' && (
                    <div>
                      {e.decisa_da}{e.ruolo_decisore ? ` (${NOMI_DECISORI[e.ruolo_decisore]})` : ''}
                      {e.decisa_il ? `, ${giorno(e.decisa_il)}` : ''}
                    </div>
                  )}
                  {e.stato === 'concessa' && (
                    <div>
                      fino al {giorno(e.fino_al)};{' '}
                      {e.consenso ? `consenso della persona: ${e.consenso_modalita}` : 'senza consenso della persona'}
                    </div>
                  )}
                  {e.nota && <div>{e.nota}</div>}
                </td>
                <td data-etichetta="">
                  {e.stato === 'in_attesa' && dati.decide_estensioni && (
                    <Bottone variante="secondario" dimensione="piccolo"
                             onClick={() => apri({ tipo: 'decidi', estensione: e, massimo: c.conserva_al_massimo })}>
                      Decidi
                    </Bottone>
                  )}
                </td>
              </tr>
            ))}
          </Tabella>
        </>
      )}
    </Riquadro>
  )
}

// --- finestre ----------------------------------------------------------------------------------

/** Invio di una finestra: errore nella finestra stessa, comando non ripetibile durante l'invio. */
function useInvio() {
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  async function invia(azione: () => Promise<void>) {
    setInCorso(true); setErrore(null)
    try { await azione() } catch (e) { setErrore(messaggioErrore(e)) } finally { setInCorso(false) }
  }
  return { inCorso, errore, invia }
}

function ModaleMotivo({ titolo, comando: testoComando, chiudi, invia }: {
  titolo: string; comando: string; chiudi: () => void; invia: (motivazione: string) => Promise<void>
}) {
  const [motivo, setMotivo] = useState('')
  const { inCorso, errore, invia: esegui } = useInvio()
  return (
    <Modale titolo={titolo} chiudi={chiudi}
      azioni={<>
        <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
        <Bottone variante="primario" inCorso={inCorso} disabled={motivo.trim().length < 5}
                 onClick={() => void esegui(() => invia(motivo.trim()))}>{testoComando}</Bottone>
      </>}>
      <p>
        L’ingresso vale 30 giorni; poi, se serve, si rientra con un nuovo motivo. Il motivo è conservato cifrato nel
        registro degli eventi, visibile al DPO e al Referente privacy.
      </p>
      <Campo etichetta="Motivo dell’ingresso" valore={motivo} cambia={setMotivo} obbligatorio maxLength={500}
             segnaposto="es. verifica della segnalazione del 28 ottobre" aiuto="Almeno cinque caratteri." />
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

function ModaleStanza({ chiudi, invia }: { chiudi: () => void; invia: (nome: string, conPersona: boolean) => Promise<void> }) {
  const [nome, setNome] = useState('')
  const [conPersona, setConPersona] = useState(false)
  const { inCorso, errore, invia: esegui } = useInvio()
  return (
    <Modale titolo="Nuova sotto-chat" chiudi={chiudi}
      azioni={<>
        <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
        <Bottone variante="primario" inCorso={inCorso} disabled={!nome.trim()}
                 onClick={() => void esegui(() => invia(nome.trim(), conPersona))}>Crea la sotto-chat</Bottone>
      </>}>
      <Avviso tipo="attenzione">
        Niente nomi di persone né altri dati che le identifichino: il nome della sotto-chat lo vede anche il server
        del messenger. Per esempio «Rete antiviolenza», «Questura», «Lavoro».
      </Avviso>
      <Campo etichetta="Nome" valore={nome} cambia={setNome} obbligatorio maxLength={60}
             aiuto={`Al massimo 60 caratteri (${nome.length}/60).`} />
      <Spunta etichetta="Con la persona assistita" valore={conPersona} cambia={setConPersona}
              aiuto="Se spuntata, nella sotto-chat entra anche la persona, quando ha il suo accesso." />
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

function ModaleConferma({ titolo, comando: testoComando, chiudi, invia, children }: {
  titolo: string; comando: string; chiudi: () => void; invia: () => Promise<void>; children: ReactNode
}) {
  const { inCorso, errore, invia: esegui } = useInvio()
  return (
    <Modale titolo={titolo} chiudi={chiudi} fuocoIniziale="azioni"
      azioni={<>
        <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
        <Bottone variante="critico" inCorso={inCorso} onClick={() => void esegui(invia)}>{testoComando}</Bottone>
      </>}>
      {children}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

/**
 * Accesso della persona assistita: si crea o si rinnova, e le credenziali si vedono una volta sola.
 * La parola d'accesso non e' nel gestionale: chiusa la finestra non si recupera, si rinnova.
 */
function ModalePersona({ chat, percorso, chiudi, nonRisponde }: {
  chat: Chat; percorso: string; chiudi: () => void; nonRisponde: () => void
}) {
  const [credenziali, setCredenziali] = useState<Credenziali | null>(null)
  const { inCorso, errore, invia } = useInvio()
  const rinnovo = !!chat.persona

  async function crea() {
    try {
      setCredenziali(await api.post<Credenziali>(percorso))
    } catch (e) {
      if (e instanceof ErroreApi && e.codice === NON_RISPONDE) { nonRisponde(); return }
      throw e
    }
  }

  if (credenziali) {
    return (
      <Modale titolo="Credenziali della persona assistita" chiudi={chiudi} fuocoIniziale="azioni"
        azioni={<Bottone variante="primario" onClick={chiudi}>Le ho consegnate: chiudi</Bottone>}>
        <Avviso tipo="attenzione">
          <strong>Si vedono solo adesso.</strong> Consegnale di persona: a voce, o scritte su un foglio da dare
          direttamente alla persona. Non mandarle per messaggio o per email. Chiusa questa finestra non si recuperano:
          si rinnova l’accesso.
        </Avviso>
        <dl className="elenco-dati">
          <Dato nome="App">{credenziali.app} (Android, iPhone o computer)</Dato>
          <Dato nome="Indirizzo del server"><span className="mono messenger-indirizzo">{credenziali.server}</span></Dato>
          <Dato nome="Nome utente"><span className="mono messenger-credenziale">{credenziali.nome_utente}</span></Dato>
          <Dato nome="Parola d’accesso"><span className="mono messenger-credenziale">{credenziali.parola}</span></Dato>
        </dl>
        <ol className="messenger-passi">
          <li>Installare {credenziali.app} sul telefono della persona.</li>
          <li>Scegliere «Accedi», cambiare il server e scrivere l’indirizzo qui sopra.</li>
          <li>Entrare con nome utente e parola d’accesso; poi impostare il backup sicuro che l’app propone.</li>
        </ol>
        <Avviso>
          Consiglia alla persona di bloccare lo schermo del telefono con un codice e di non lasciarlo incustodito: chi
          ha il telefono in mano legge la chat.
        </Avviso>
      </Modale>
    )
  }

  return (
    <Modale titolo="Accesso della persona" chiudi={chiudi}
      azioni={<>
        <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
        <Bottone variante="primario" inCorso={inCorso} onClick={() => void invia(crea)}>
          {rinnovo ? 'Rinnova l’accesso' : 'Crea l’accesso'}
        </Bottone>
      </>}>
      <p>
        La persona entra nel messenger con l’app Element, con un nome utente e una parola d’accesso che il
        gestionale genera adesso e mostra una volta sola, per consegnarli di persona. Vede solo le sotto-chat «con la
        persona»; nel messenger compare come «Persona assistita».
      </p>
      {rinnovo && (
        <Avviso tipo="attenzione">
          La persona ha già un accesso: rinnovandolo, la parola d’accesso di prima non vale più.
        </Avviso>
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

/**
 * Campi del consenso della persona: si', con il modo in cui e' stato raccolto; no, solo il DPO o il
 * Referente privacy, con motivazione. La Direzione concede solo con il consenso.
 */
type Consenso = { consenso: '' | 'si' | 'no'; modalita: string; nota: string }

function CampiConsenso({ ruolo, valore, cambia }: { ruolo: Decisore; valore: Consenso; cambia: (v: Consenso) => void }) {
  return (
    <>
      <Scelta etichetta="Consenso della persona assistita" valore={valore.consenso} obbligatorio vuota="Scegli"
              cambia={(v) => cambia({ ...valore, consenso: v as Consenso['consenso'] })}
              opzioni={[{ valore: 'si', etichetta: 'Sì, la persona ha dato il consenso' },
                        { valore: 'no', etichetta: 'No, senza consenso' }]} />
      {valore.consenso === 'si' && (
        <AreaTesto etichetta="Come è stato raccolto" valore={valore.modalita} cambia={(v) => cambia({ ...valore, modalita: v })}
                   obbligatorio righe={2} maxLength={500} segnaposto="es. colloquio del 29/10 con la mediatrice, modulo firmato" />
      )}
      {valore.consenso === 'no' && (senzaConsenso(ruolo)
        ? (
          <AreaTesto etichetta="Motivazione" valore={valore.nota} cambia={(v) => cambia({ ...valore, nota: v })}
                     obbligatorio righe={3} maxLength={1000} aiuto="Senza consenso l’estensione si concede solo con una motivazione (almeno dieci caratteri)." />
        )
        : <Avviso tipo="attenzione">Senza il consenso della persona l’estensione la concede solo il Referente privacy (o il DPO).</Avviso>)}
    </>
  )
}

const consensoValido = (ruolo: Decisore, c: Consenso) =>
  c.consenso === 'si' ? c.modalita.trim().length >= 5 : c.consenso === 'no' && senzaConsenso(ruolo) && c.nota.trim().length >= 10

const corpoConsenso = (c: Consenso) => ({
  consenso: c.consenso === 'si',
  consenso_modalita: c.consenso === 'si' ? c.modalita.trim() : null,
  nota: c.nota.trim() || null,
})

function ModaleEstensione({ chat, decisore, anniMassimi, percorso, chiudi, fatto }: {
  chat: Chat; decisore: Decisore | null; anniMassimi: number; percorso: string
  chiudi: () => void; fatto: (e: Esito) => void
}) {
  const [fino, setFino] = useState('')
  const [motivazione, setMotivazione] = useState('')
  // Chi non e' nel personale (la Direzione senza ingresso) puo' solo concedere.
  const [concedi, setConcedi] = useState(!chat.personale)
  const [consenso, setConsenso] = useState<Consenso>({ consenso: '', modalita: '', nota: '' })
  const { inCorso, errore, invia } = useInvio()
  const pronta = !!fino && motivazione.trim().length >= 10 && (!concedi || (!!decisore && consensoValido(decisore, consenso)))

  async function manda() {
    await api.post(percorso, { fino_al: fino, motivazione: motivazione.trim(), ...(concedi ? { concedi: corpoConsenso(consenso) } : {}) })
    fatto({ testo: concedi ? `Estensione concessa: la chat si conserva fino al ${giorno(fino)}.`
      : 'Richiesta inviata: la decidono la Direzione, il DPO o il Referente privacy.' })
  }

  return (
    <Modale titolo={concedi ? 'Concedi un’estensione' : 'Chiedi un’estensione'} chiudi={chiudi}
      azioni={<>
        <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
        <Bottone variante="primario" inCorso={inCorso} disabled={!pronta} onClick={() => void invia(manda)}>
          {concedi ? 'Concedi' : 'Invia la richiesta'}
        </Bottone>
      </>}>
      <p>
        Oggi la chat si conserva fino al {giorno(chat.conserva_fino_al)}. Si può estendere al massimo fino al{' '}
        {giorno(chat.conserva_al_massimo)} ({anniMassimi} anni dalla chiusura), con il consenso della persona assistita.
      </p>
      <Campo etichetta="Conservare fino al" tipo="date" valore={fino} cambia={setFino} obbligatorio
             min={chat.conserva_fino_al ?? undefined} max={chat.conserva_al_massimo ?? undefined} />
      <AreaTesto etichetta="Motivo" valore={motivazione} cambia={setMotivazione} obbligatorio righe={3} maxLength={1000}
                 aiuto="Almeno dieci caratteri. È conservato cifrato." />
      {decisore && chat.personale && (
        <Spunta etichetta="Concedila subito" valore={concedi} cambia={setConcedi}
                aiuto="Direzione, DPO e Referente privacy possono concedere l’estensione direttamente, senza una richiesta." />
      )}
      {concedi && decisore && <CampiConsenso ruolo={decisore} valore={consenso} cambia={setConsenso} />}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

/** Decisione su una richiesta di estensione: nel riquadro della scheda e nella pagina Approvazioni. */
export function ModaleDecisione({ estensione, massimo, ruolo, chiudi, fatto }: {
  estensione: Pick<Estensione, 'id' | 'fino_al_richiesto' | 'motivazione' | 'richiesta_da'>
  massimo: string | null; ruolo: Decisore; chiudi: () => void; fatto: (testo: string) => void
}) {
  const [decisione, setDecisione] = useState<'concessa' | 'respinta'>('concessa')
  const [fino, setFino] = useState(estensione.fino_al_richiesto)
  const [consenso, setConsenso] = useState<Consenso>({ consenso: '', modalita: '', nota: '' })
  const [motivoRifiuto, setMotivoRifiuto] = useState('')
  const { inCorso, errore, invia } = useInvio()
  const pronta = decisione === 'respinta' ? motivoRifiuto.trim().length >= 5 : !!fino && consensoValido(ruolo, consenso)

  async function manda() {
    const corpo = decisione === 'respinta'
      ? { decisione, nota: motivoRifiuto.trim() }
      : { decisione, fino_al: fino, ...corpoConsenso(consenso) }
    await api.post(`/chat-riservate/estensioni/${estensione.id}/decidi`, corpo)
    fatto(decisione === 'concessa' ? `Estensione concessa fino al ${giorno(fino)}.` : 'Richiesta di estensione respinta.')
  }

  return (
    <Modale titolo="Estensione della conservazione" chiudi={chiudi}
      azioni={<>
        <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
        <Bottone variante={decisione === 'respinta' ? 'critico' : 'primario'} inCorso={inCorso} disabled={!pronta}
                 onClick={() => void invia(manda)}>
          {decisione === 'respinta' ? 'Respingi' : 'Concedi'}
        </Bottone>
      </>}>
      <p className="piccolo tenue">Chiesta da {estensione.richiesta_da}, fino al {giorno(estensione.fino_al_richiesto)}.</p>
      <p className="testo-libero">{estensione.motivazione}</p>
      <Scelta etichetta="Decisione" valore={decisione} cambia={(v) => setDecisione(v as 'concessa' | 'respinta')}
              opzioni={[{ valore: 'concessa', etichetta: 'Concedi' }, { valore: 'respinta', etichetta: 'Respingi' }]} />
      {decisione === 'concessa'
        ? (
          <>
            <Campo etichetta="Conservare fino al" tipo="date" valore={fino} cambia={setFino} obbligatorio
                   max={massimo ?? undefined}
                   aiuto={massimo ? `Al massimo fino al ${giorno(massimo)}: dieci anni dalla chiusura.` : undefined} />
            <CampiConsenso ruolo={ruolo} valore={consenso} cambia={setConsenso} />
          </>
        )
        : <AreaTesto etichetta="Motivo del rifiuto" valore={motivoRifiuto} cambia={setMotivoRifiuto} obbligatorio righe={3} maxLength={1000} />}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

// --- pagina Approvazioni -----------------------------------------------------------------------

type RichiestaDaDecidere = {
  id: string; chat_id: string; persona_id: string; numero_scheda: number; fino_al_richiesto: string
  motivazione: string; richiesta_il: string; richiesta_da: string; chiusa_il: string
  conserva_fino_al: string; conserva_al_massimo: string
}

/**
 * Estensioni della conservazione delle chat riservate in attesa, per Direzione, DPO e Referente
 * privacy. DPO e Referente privacy non aprono le schede: le decidono da qui.
 */
export function EstensioniChatRiservate() {
  const { io } = useSessione()
  const ruoli = new Set(io?.incarichi.map((i) => i.ruolo) ?? [])
  // Lo stesso ordine dell'API: DPO e Referente privacy, che possono concedere anche senza consenso,
  // prima della Direzione.
  const ruolo = (['DPO', 'RPR', 'DIR'] as const).find((r) => ruoli.has(r)) ?? null
  const [elenco, setElenco] = useState<RichiestaDaDecidere[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [scelta, setScelta] = useState<RichiestaDaDecidere | null>(null)

  const carica = useCallback(() => {
    if (!ruolo) return
    api.get<RichiestaDaDecidere[]>('/chat-riservate/estensioni').then(setElenco).catch((e) => setErrore(messaggioErrore(e)))
  }, [ruolo])
  useEffect(carica, [carica])

  if (!ruolo || (!elenco?.length && !errore && !esito)) return null

  return (
    <Riquadro titolo="Conservazione delle chat riservate">
      {esito && <Avviso tipo="successo">{esito}</Avviso>}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {elenco && elenco.length > 0 && (
        <Tabella intestazioni={['Scheda', 'Richiesta', 'Motivo', '']}>
          {elenco.map((r) => (
            <tr key={r.id}>
              <td data-etichetta="Scheda">
                n. {r.numero_scheda}
                <div className="piccolo tenue">chiusa il {giorno(r.chiusa_il)}, conservata fino al {giorno(r.conserva_fino_al)}</div>
              </td>
              <td data-etichetta="Richiesta" className="piccolo">
                fino al {giorno(r.fino_al_richiesto)}
                <div className="tenue">{r.richiesta_da} · {giorno(r.richiesta_il)}</div>
              </td>
              <td data-etichetta="Motivo" className="testo-libero">{r.motivazione}</td>
              <td data-etichetta="">
                <Bottone variante="secondario" dimensione="piccolo" onClick={() => { setEsito(null); setScelta(r) }}>Decidi</Bottone>
              </td>
            </tr>
          ))}
        </Tabella>
      )}
      {scelta && (
        <ModaleDecisione estensione={scelta} massimo={scelta.conserva_al_massimo} ruolo={ruolo}
                         chiudi={() => setScelta(null)}
                         fatto={(testo) => { setScelta(null); setEsito(testo); carica() }} />
      )}
    </Riquadro>
  )
}
